import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useWishlist, useRemoveFromWishlist } from '../hooks/useProducts';
import { useAuthStore } from '../stores/authStore';

export default function WishlistPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const { data: wishlist, isLoading } = useWishlist();
  const removeFromWishlist = useRemoveFromWishlist();

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="max-w-md mx-auto">
          <svg
            className="w-24 h-24 mx-auto text-gray-300 mb-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('wishlist.title')}</h1>
          <p className="text-gray-500 mb-6">{t('auth.viewProducts')}</p>
          <Link to="/login" className="btn btn-primary">
            {t('auth.login')}
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">{t('wishlist.title')}</h1>
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleRemove = (productId: string) => {
    removeFromWishlist.mutate(productId);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">{t('wishlist.title')}</h1>

      {wishlist && wishlist.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-start">{t('wishlist.productName')}</th>
                <th className="px-6 py-3 text-start">{t('wishlist.unitPrice')}</th>
                <th className="px-6 py-3 text-center w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {wishlist.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/product/${product.slug}`}
                      className="flex items-center gap-4 group"
                    >
                      <img
                        src={product.images?.[0] || '/placeholder-product.jpg'}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <span className="group-hover:text-primary transition-colors">
                        {product.name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-primary">
                      ₪{product.price.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleRemove(product.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={t('wishlist.remove')}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="w-24 h-24 mx-auto text-gray-300 mb-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <p className="text-gray-500 mb-6">{t('wishlist.empty')}</p>
          <Link to="/" className="btn btn-primary">
            {t('home.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}
